import { useMemo, useState } from 'react';
import { IconDownload, IconLayoutSidebarRight, IconPlus, IconSearch, IconDots } from '@tabler/icons-react';
import { HeaderTool, Page, PageHeader } from '@hana/shell';
import { useT } from '@hana/i18n';
import { Button, Segmented, Skeleton, StateView, useToast, type ScreenState } from '@hana/ui';
import {
  DECLS, GAP_MONTH, HSN, HS_CHAPTER, MONTHS, TERM_GROUPS, TERM_LABEL, TM_LABEL, groupBy, mLabel, nf, pct1, risers, select, sumS, teu, termG, topOf,
  type Decl, type FilterKey, type Filters, type Mode,
} from './marketShare';
import { useWide } from './useWide';
import './marketShare.i18n';
import s from './marketShare.module.css';

// Mint-MarketShare (1536 / 1920): Thị phần từ tờ khai. Tổng quan đủ khối; Shipper · Consignee · Tờ khai dạng bảng gọn; cột phải 372 ở 1920.
// Chỉ đọc; nút ghi báo toast. Dữ liệu mẫu sinh theo script board (tên doanh nghiệp minh hoạ).

type View = 'ov' | 'sh' | 'cn' | 'car' | 'dec';
const MODES: readonly Mode[] = ['SEA', 'AIR', 'ROAD', 'OTH'];
const MC: Record<Mode, string> = { SEA: 'var(--d-blue)', AIR: 'var(--d-violet)', ROAD: 'var(--d-green)', OTH: 'var(--d-gray)' };
const TGC: Record<string, string> = { E: 'var(--d-gray)', F: 'var(--lb)', C: 'var(--d-blue)', D: 'var(--d-green)' };
const RANGES: readonly (readonly [number, string])[] = [[3, '3 tháng'], [6, '6 tháng'], [12, '12 tháng'], [0, 'Tất cả']];
const FDEF: Record<Exclude<FilterKey, 'sh' | 'cn' | 'month'>, { label: string; val: (r: Decl) => string; text: (v: string) => string }> = {
  mode: { label: 'Phương thức', val: (r) => r.tm, text: (v) => TM_LABEL[v as Mode] ?? v },
  ctry: { label: 'Nước nhập', val: (r) => r.ic, text: (v) => v },
  hs: { label: 'HS', val: (r) => r.hs.slice(0, 2), text: (v) => `Chương ${v} · ${HS_CHAPTER[v] ?? ''}` },
  term: { label: 'Incoterm', val: (r) => termG(r.tr), text: (v) => `Nhóm ${v}` },
  cu: { label: 'Chi cục / cảng', val: (r) => r.cu, text: (v) => v },
};
const chipText = (k: FilterKey, v: string) => (k === 'month' ? `Tháng: ${mLabel(v)}` : k === 'sh' ? `Shipper: ${v}` : k === 'cn' ? `Consignee: ${v}` : `${FDEF[k].label}: ${FDEF[k].text(v)}`);

function readState(): ScreenState | null {
  const v = new URLSearchParams(location.search).get('state');
  return v === 'loading' || v === 'empty' || v === 'error' || v === 'noperm' ? v : null;
}

function spark(h: readonly number[]) {
  const mn = Math.min(...h), mx = Math.max(...h), r = mx - mn || 1;
  return h.map((v, i) => `${i ? 'L' : 'M'}${(2 + (i * 116) / Math.max(1, h.length - 1)).toFixed(1)} ${(21 - ((v - mn) / r) * 18).toFixed(1)}`).join(' ');
}

interface Props { rows: readonly Decl[]; all: readonly Decl[]; range: number; q: string; flt: Filters; metric: 'dec' | 'teu'; setMetric: (m: 'dec' | 'teu') => void; toggle: (k: FilterKey, v: string) => void; setFlt: (f: Filters) => void; goView: (v: View) => void; setQ: (q: string) => void }

function Overview({ rows, all, range, q, flt, metric, setMetric, toggle, setFlt, goView, setQ }: Props) {
  const tx = useT();
  const shG = groupBy(rows, 'sh');
  const tot = sumS(rows) || 1;
  const noMonth = select(all, { range, q, flt }, 'month');
  const months = MONTHS.filter((m) => !range || MONTHS.indexOf(m) >= MONTHS.indexOf('2026-09') - range + 1);
  const mVal = (xs: readonly Decl[]) => (metric === 'teu' ? teu(xs) : sumS(xs));
  const mMax = Math.max(1, ...months.map((m) => mVal(noMonth.filter((r) => r.m === m))));
  const spM = MONTHS.filter((m) => m !== GAP_MONTH);
  const skip = select(all, { range, q, flt }, 'month');
  const spS = spM.map((m) => sumS(skip.filter((r) => r.m === m)));
  const spT = spM.map((m) => teu(skip.filter((r) => r.m === m)));
  const kpis: readonly { k: string; v: string; u: string; sub: string; sp?: readonly number[] }[] = [
    { k: 'Tờ khai (lô)', v: nf(sumS(rows)), u: '', sub: `${rows.length} dòng gộp`, sp: spS },
    { k: 'Shipper', v: String(shG.size), u: '', sub: `${[...shG.values()].filter((xs) => xs[0]!.cus).length} đã là khách HANA` },
    { k: 'Consignee', v: String(groupBy(rows, 'cn').size), u: '', sub: '' },
    { k: 'Nước nhập', v: String(new Set(rows.map((r) => r.ic)).size), u: '', sub: '' },
    { k: 'TEU ước tính', v: nf(teu(rows)), u: 'TEU', sub: '', sp: spT },
  ];
  const tops = [
    { k: 'Top shipper', key: 'sh', more: 'Xem tất cả', go: () => goView('sh'), fmt: (k: string) => k, pick: (raw: string) => setQ(raw) },
    { k: 'Top nước nhập', key: 'ic', more: 'Bỏ lọc nước', go: () => setFlt({ ...flt, ctry: '' }), fmt: (k: string) => k, pick: (raw: string) => toggle('ctry', raw) },
    { k: 'Top HS (4 số)', key: 'hs', more: 'Theo tên hàng', go: () => goView('dec'), fmt: (k: string) => `${k} · ${HSN[k]}`, pick: (raw: string) => toggle('hs', raw.slice(0, 2)) },
    { k: 'Top consignee', key: 'cn', more: 'Xem tất cả', go: () => goView('cn'), fmt: (k: string) => k, pick: (raw: string) => setQ(raw) },
    { k: 'Top chi cục / cảng xuất', key: 'cu', more: 'Bỏ lọc cảng', go: () => setFlt({ ...flt, cu: '' }), fmt: (k: string) => k, pick: (raw: string) => toggle('cu', raw) },
    { k: 'Top tên hàng (Commodity)', key: 'cm', more: 'Xem tờ khai', go: () => goView('dec'), fmt: (k: string) => k, pick: (raw: string) => setQ(raw) },
  ] as const;
  const tg = TERM_GROUPS.map((g) => [g, sumS(rows.filter((r) => termG(r.tr) === g))] as const);
  return (
    <>
      <section aria-label="Chỉ số" className={s.kpis}>
        {kpis.map((k) => (
          <div key={k.k} className={s.kpi}>
            <div className={s.kLb}>{k.k}</div>
            <div className={s.kVal}><b>{k.v}</b><span>{k.u}</span></div>
            <div className={s.kSub}>{k.sub}</div>
            {k.sp && k.sp.length > 1 && (
              <svg role="img" aria-label={`${k.k} theo tháng: ${k.sp.map(nf).join(', ')}`} className={s.spark} width="100%" height="24" viewBox="0 0 120 24" preserveAspectRatio="none">
                <path d={spark(k.sp)} fill="none" stroke="var(--d-blue)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            )}
          </div>
        ))}
      </section>
      <section aria-label="Phương thức vận tải" className={s.modes}>
        {MODES.map((m) => {
          const xs = rows.filter((r) => r.tm === m);
          return (
            <button key={m} type="button" className={s.mode} aria-pressed={flt.mode === m} style={{ ['--mc' as string]: MC[m] }} onClick={() => toggle('mode', m)}>
              <span className={s.mHead}><b>{TM_LABEL[m]}</b><b style={{ color: MC[m] }}>{pct1(sumS(xs), tot)}</b></span>
              <span className={s.tx2}>{nf(sumS(xs))} tờ khai · {nf(teu(xs))} TEU</span>
              <span className={s.mTrack}><i style={{ width: pct1(sumS(xs), tot) }} /></span>
            </button>
          );
        })}
      </section>
      <section aria-label="Xu hướng theo tháng" className={`${s.card} ${s.trend}`}>
        <div className={s.tHead}>
          <h2>{tx('mshare.trend')}</h2>
          <Segmented kind="toggle" label="Chỉ số" value={metric} onChange={setMetric} items={[{ key: 'dec', label: 'Tờ khai' }, { key: 'teu', label: 'TEU' }]} />
        </div>
        <div className={s.bars} role="group" aria-label="Tờ khai theo tháng và phương thức">
          {months.map((m) => {
            const xs = noMonth.filter((r) => r.m === m);
            const gap = m === GAP_MONTH;
            const v = mVal(xs);
            const tip = gap ? `${mLabel(m)} · chưa nạp dữ liệu` : `${mLabel(m)} · ${nf(v)}${metric === 'teu' ? ' TEU' : ' tờ khai'}`;
            return (
              <button key={m} type="button" className={s.bar} title={tip} aria-label={tip} aria-pressed={flt.month === m} data-dim={!!flt.month && flt.month !== m} onClick={() => !gap && toggle('month', m)}>
                <span className={s.bV}>{gap ? '' : nf(v)}</span>
                {gap ? <span className={s.gap}>{tx('mshare.gap')}</span> : (['OTH', 'ROAD', 'AIR', 'SEA'] as const).map((k) => {
                  const h = Math.round((mVal(xs.filter((r) => r.tm === k)) / mMax) * 170);
                  return h ? <span key={k} className={s.seg} style={{ height: h, background: MC[k] }} /> : null;
                })}
              </button>
            );
          })}
        </div>
        <div className={s.mons}>{months.map((m) => <span key={m}>{mLabel(m)}</span>)}</div>
        <div className={s.legend}>{MODES.map((m) => <span key={m}><i style={{ background: MC[m] }} />{TM_LABEL[m]}</span>)}</div>
      </section>
      <div className={s.tops}>
        {tops.map((g) => (
          <section key={g.k} aria-label={g.k} className={`${s.card} ${s.top}`}>
            <div className={s.topHd}><h2>{g.k}</h2><button type="button" className={s.link} style={{ height: 'auto', padding: 0 }} onClick={g.go}>{g.more}</button></div>
            {topOf(rows, g.key, 6).map((r) => (
              <button key={r.raw} type="button" className={s.topRow} title={`Bấm để lọc / xem ${g.fmt(r.raw)}`} onClick={() => g.pick(r.raw)}>
                <span className={s.rk}>{r.i}</span>
                <span style={{ minWidth: 0 }}><span className={s.nm}>{g.fmt(r.raw)}</span><span className={s.track}><i style={{ width: `${r.w}%` }} /></span></span>
                <span className={s.rv}><b>{nf(r.v)}</b><span>{r.pct}</span></span>
              </button>
            ))}
          </section>
        ))}
      </div>
      <section aria-label="Điều kiện giao hàng" className={`${s.card} ${s.term}`}>
        <b>{tx('mshare.inco')}</b><span className={s.lbl}>{tx('mshare.incoHint')}</span>
        <span style={{ flexGrow: 1 }} />
        <div className={s.tBar} role="img" aria-label={`Tờ khai theo nhóm Incoterm: ${tg.map(([g, n]) => `${g} ${nf(n)}`).join(', ')}`}>
          {tg.filter(([, n]) => n > 0).map(([g, n]) => <div key={g} title={`${g} · ${n}`} style={{ flex: `${n} 1 0`, background: TGC[g] }} />)}
        </div>
        {TERM_GROUPS.map((g) => {
          const n = sumS(rows.filter((r) => termG(r.tr) === g));
          return (
            <button key={g} type="button" className={s.tBtn} aria-pressed={flt.term === g} onClick={() => toggle('term', g)}>
              <i style={{ background: TGC[g] }} />{g} ({TERM_LABEL[g]}) · {nf(n)} ( {Math.round((n * 100) / tot)}% )
            </button>
          );
        })}
      </section>
    </>
  );
}

function Grouped({ rows, by, onOpen }: { rows: readonly Decl[]; by: 'sh' | 'cn'; onOpen: (k: string) => void }) {
  const tx = useT();
  const list = [...groupBy(rows, by)].map(([k, xs]) => ({ k, xs, n: sumS(xs) })).sort((a, b) => b.n - a.n);
  return (
    <section className={s.card} aria-label={by === 'sh' ? 'Shipper' : 'Consignee'}>
      <table className={s.table}>
        <colgroup><col style={{ width: 52 }} /><col /><col style={{ width: 176 }} /><col style={{ width: 92 }} /><col style={{ width: 84 }} /><col style={{ width: 230 }} /></colgroup>
        <thead><tr><th>#</th><th>{by === 'sh' ? 'Shipper' : 'Consignee'}</th><th>CRM</th><th className={s.r}>{tx('mshare.decl')}</th><th className={s.r}>TEU</th><th>{by === 'sh' ? tx('mshare.mainCountry') : tx('mshare.country')}</th></tr></thead>
        <tbody>
          {list.map(({ k, xs, n }, i) => (
            <tr key={k} onClick={() => onOpen(k)} style={{ cursor: 'pointer' }}>
              <td className={s.lbl}>{i + 1}</td>
              <td><span className={s.b}>{k}</span><span className={s.sub}>{by === 'sh' ? `MST ${xs[0]!.tx} · ${xs[0]!.cu}` : xs[0]!.ic}</span></td>
              <td className={s.tx2}>{by === 'sh' ? (xs[0]!.cus ? `Khách ${xs[0]!.cus}` : 'Chưa có trong CRM') : 'Bên mua'}</td>
              <td className={s.r}>{nf(n)}</td>
              <td className={s.r}>{nf(teu(xs))}</td>
              <td className={s.tx2}>{topOf(xs, 'ic', 2).map((x) => x.raw).join(', ')}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

function Decls({ rows }: { rows: readonly Decl[] }) {
  const tx = useT();
  const [page, setPage] = useState(1);
  const sorted = useMemo(() => [...rows].sort((a, b) => (b.m + b.d.slice(0, 2)).localeCompare(a.m + a.d.slice(0, 2))), [rows]);
  return (
    <section className={s.card} aria-label="Tờ khai">
      <table className={s.table}>
        <colgroup><col style={{ width: 150 }} /><col style={{ width: 280 }} /><col style={{ width: 220 }} /><col style={{ width: 200 }} /><col style={{ width: 100 }} /><col /></colgroup>
        <thead><tr><th>{tx('mshare.dDate')}</th><th>{tx('mshare.dShipper')}</th><th>{tx('mshare.dCons')}</th><th>{tx('mshare.dHs')}</th><th className={s.r}>{tx('mshare.dLot')}</th><th>{tx('mshare.dInco')}</th></tr></thead>
        <tbody>
          {sorted.slice(0, page * 12).map((r) => (
            <tr key={r.id}>
              <td><span className={s.b}>{r.d}</span><span className={s.sub}>{r.no}</span></td>
              <td><span className={s.b}>{r.sh}</span><span className={s.sub}>{r.tx}</span></td>
              <td><span className={s.b}>{r.cn}</span><span className={s.sub}>{r.ic}</span></td>
              <td><span className={s.b}>{r.hs}</span><span className={s.sub}>{r.cm}</span></td>
              <td className={s.r}>{r.s}</td>
              <td className={s.tx2}>{r.tr} · {TM_LABEL[r.tm]}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {sorted.length > page * 12 && <Button className={s.more} onClick={() => setPage((p) => p + 1)}>{tx('mshare.more')}</Button>}
    </section>
  );
}

export function MarketShare() {
  const tx = useT();
  const toast = useToast();
  const wide = useWide();
  const forced = readState();
  const [view, setView] = useState<View>('ov');
  const [range, setRange] = useState(12);
  const [q, setQ] = useState('');
  const [flt, setFlt] = useState<Filters>({});
  const [metric, setMetric] = useState<'dec' | 'teu'>('dec');
  const [fOpen, setFOpen] = useState(false);
  const [fSec, setFSec] = useState<keyof typeof FDEF>('ctry');
  const [dockOpen, setDockOpen] = useState(true);

  const query = { range, q, flt };
  const rows = useMemo(() => select(DECLS, query), [range, q, flt]); // eslint-disable-line react-hooks/exhaustive-deps
  const toggle = (k: FilterKey, v: string) => setFlt((f) => ({ ...f, [k]: f[k] === v ? '' : v }));
  const chips = (Object.keys(flt) as FilterKey[]).filter((k) => flt[k]);
  const hasFlt = chips.length > 0 || !!q || range !== 12;
  const clearAll = () => { setFlt({}); setQ(''); setRange(12); };

  const shN = groupBy(rows, 'sh').size;
  const cnN = groupBy(rows, 'cn').size;
  let state: ScreenState = forced ?? 'ready';
  if (state === 'ready' && rows.length === 0) state = 'empty';
  const ready = state === 'ready';
  const soon = (what: string) => () => toast({ msg: `${what} chưa mở ở bản này` });
  const csvN = view === 'sh' ? `${shN} shipper` : view === 'cn' ? `${cnN} consignee` : `${rows.length} dòng`;

  const optVals = [...new Set(select(DECLS, query, fSec).map(FDEF[fSec].val))].sort();
  const body = () => {
    if (view === 'sh') return <Grouped rows={rows} by="sh" onOpen={(k) => { setQ(k); setView('dec'); }} />;
    if (view === 'cn') return <Grouped rows={rows} by="cn" onOpen={(k) => { setQ(k); setView('dec'); }} />;
    if (view === 'dec') return <Decls rows={rows} />;
    if (view === 'car') return <section className={s.card} aria-label={tx('mshare.carByAria')}><div className={s.dkNone} style={{ margin: 16 }}>{tx('mshare.carNo')}</div></section>;
    return <Overview rows={rows} all={DECLS} range={range} q={q} flt={flt} metric={metric} setMetric={setMetric} toggle={toggle} setFlt={setFlt} goView={setView} setQ={setQ} />;
  };
  const tabs = [
    { key: 'ov', label: 'Tổng quan' },
    { key: 'sh', label: 'Shipper', n: shN },
    { key: 'cn', label: 'Consignee', n: cnN },
    { key: 'car', label: 'Theo hãng', n: 5 },
    { key: 'dec', label: 'Tờ khai', n: sumS(rows) },
  ] as const;

  const dockEl = (
          <aside className={s.dock} aria-label="Theo dõi & khách tiềm năng">
            <b className={s.dkT}>{tx('mshare.leads')}</b>
            <div className={s.dkNone}>{tx('mshare.leadsNone')}</div>
            <div className={s.dkSep} />
            <b style={{ fontWeight: 600 }} title={tx('mshare.risingTip')}>{tx('mshare.rising')}</b>
            <div>
              {risers(DECLS).map((r) => (
                <button key={r.k} type="button" className={s.dkRise} style={{ width: '100%' }} onClick={() => { setQ(r.k); setView('dec'); }}>
                  <span>{r.k}</span><span>{r.d}</span>
                </button>
              ))}
            </div>
          </aside>
  );

  return (
    <Page kind="table" dock={ready && wide && dockOpen ? dockEl : undefined}>
      <PageHeader
        title="Thị phần"
        tabs={<Segmented label="Cách xem" value={view} onChange={setView} items={tabs} />}
        actions={
          <>
            <HeaderTool label="Theo dõi & khách tiềm năng" icon={IconLayoutSidebarRight} active={wide && dockOpen} onClick={wide ? () => setDockOpen((o) => !o) : soon('Khách tiềm năng')} />
            <HeaderTool label="Thao tác khác" icon={IconDots} onClick={soon('Menu thao tác')} />
            <Button variant="primary" icon={<IconDownload size={16} aria-hidden="true" />} onClick={soon('Xuất CSV')}>Xuất CSV · {csvN}</Button>
          </>
        }
      />
        <div className={s.page}>
          <div className={s.tb}>
            <label className={s.search}>
              <IconSearch size={15} aria-hidden="true" />
              <input aria-label="Tìm shipper, consignee, MST, HS, tên hàng" placeholder="Shipper / consignee / MST / HS / tên hàng" value={q} onChange={(e) => setQ(e.target.value)} />
            </label>
            <Segmented kind="toggle" label="Khoảng thời gian" value={String(range)} onChange={(v) => setRange(Number(v))} items={RANGES.map(([v, k]) => ({ key: String(v), label: k }))} />
            {chips.slice(0, 2).map((k) => (
              <span key={k} className={s.chip}>{chipText(k, flt[k]!)}<button type="button" aria-label={`Bỏ lọc ${chipText(k, flt[k]!)}`} onClick={() => setFlt((f) => ({ ...f, [k]: '' }))}>×</button></span>
            ))}
            {chips.length > 2 && <span className={s.chip} style={{ padding: '0 12px' }}>+{chips.length - 2}</span>}
            <Button icon={<IconPlus size={16} aria-hidden="true" />} onClick={() => setFOpen((o) => !o)}>{tx('mshare.filter')}</Button>
            {fOpen && (
              <div className={s.pop} style={{ left: 'auto', marginLeft: 0 }} role="dialog" aria-label="Lọc tờ khai">
                <div className={s.popTabs} role="tablist" aria-label="Trường lọc">
                  {(Object.keys(FDEF) as (keyof typeof FDEF)[]).map((k) => <button key={k} type="button" role="tab" aria-selected={fSec === k} onClick={() => setFSec(k)}>{FDEF[k].label}</button>)}
                </div>
                <div className={s.opts}>
                  {optVals.map((v) => (
                    <label key={v} className={`${s.opt} ${flt[fSec] === v ? s.optOn : ''}`}>
                      <input type="radio" name="msf" checked={flt[fSec] === v} onChange={() => toggle(fSec, v)} />
                      <span>{FDEF[fSec].text(v)}</span><span>{nf(sumS(select(DECLS, query, fSec).filter((r) => FDEF[fSec].val(r) === v)))}</span>
                    </label>
                  ))}
                </div>
                <div className={s.popFoot}><span>{nf(sumS(rows))} tờ khai</span><Button onClick={() => setFOpen(false)}>Xong</Button></div>
              </div>
            )}
            {hasFlt && <button type="button" className={s.link} onClick={clearAll}>{tx('mshare.clear')}</button>}
            <span className={s.sum}>{ready ? `${nf(sumS(rows))} tờ khai · ${nf(teu(rows))} TEU` : ''}</span>
          </div>
          <StateView
            state={state}
            title={state === 'empty' ? 'Không có tờ khai khớp bộ lọc' : undefined}
            action={state === 'empty' ? <Button onClick={clearAll}>{tx('mshare.clear')}</Button> : state === 'error' ? <Button onClick={() => location.reload()}>{tx('mshare.retry')}</Button> : undefined}
            skeleton={<Skeleton rows={6} />}
          >
            {body()}
          </StateView>
        </div>
    </Page>
  );
}
